import { Controller } from "@hotwired/stimulus"
import { waitForTurnstile } from "../utils/turnstile"

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const EMAIL_ERROR_MESSAGE = "Please enter a valid email address."

export default class extends Controller {
  static targets = ["email", "consent", "submit", "turnstile", "emailError"]
  static values = { siteKey: String }

  connect() {
    this.pendingSubmit = false
    this.widgetId = null
    this.disconnected = false
    this.submitTimeout = null
    this.loadAndRenderTurnstile()
    this.syncSubmit()
  }

  disconnect() {
    this.disconnected = true
    this.clearSubmitTimeout()
    if (this.widgetId !== null && window.turnstile) {
      window.turnstile.remove(this.widgetId)
    }
  }

  syncSubmit() {
    this.submitTarget.disabled = !this.formReady() || this.pendingSubmit
  }

  validateEmail() {
    if (this.emailTarget.value.trim() !== "" && !this.emailValid()) {
      this.showEmailError()
    } else {
      this.clearEmailError()
    }
    this.syncSubmit()
  }

  submit(event) {
    if (!this.emailValid()) {
      event.preventDefault()
      this.showEmailError()
      this.emailTarget.focus()
      this.pendingSubmit = false
      this.syncSubmit()
      return
    }

    if (this.currentToken()) return

    event.preventDefault()
    this.pendingSubmit = true
    this.syncSubmit()

    if (!this.executeTurnstile()) {
      this.pendingSubmit = false
      this.syncSubmit()
    }
  }

  loadAndRenderTurnstile() {
    waitForTurnstile().then(() => {
      if (this.disconnected || this.widgetId !== null) return

      this.turnstileTarget.replaceChildren()
      this.widgetId = window.turnstile.render(this.turnstileTarget, {
        sitekey: this.siteKeyValue,
        size: "flexible",
        appearance: "interaction-only",
        execution: "execute",
        theme: "light",
        language: "en",
        callback: (token) => this.handleToken(token),
        "expired-callback": () => this.clearToken(),
        "error-callback": () => this.clearToken(),
      })
    }).catch(() => {
      this.pendingSubmit = false
      this.syncSubmit()
    })
  }

  executeTurnstile() {
    if (!window.turnstile || this.widgetId === null) return false

    window.turnstile.execute(this.widgetId)
    this.submitTimeout = window.setTimeout(() => {
      this.pendingSubmit = false
      this.syncSubmit()
    }, 15000)
    return true
  }

  handleToken(token) {
    this.clearSubmitTimeout()
    this.setToken(token)

    if (this.currentToken() && this.pendingSubmit) {
      this.pendingSubmit = false
      this.syncSubmit()
      this.element.requestSubmit()
    }
  }

  clearToken() {
    this.clearSubmitTimeout()
    this.setToken("")
    this.pendingSubmit = false
    this.syncSubmit()
  }

  clearSubmitTimeout() {
    if (!this.submitTimeout) return

    window.clearTimeout(this.submitTimeout)
    this.submitTimeout = null
  }

  setToken(token) {
    let input = this.element.querySelector("input[name='cf-turnstile-response']")
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = "cf-turnstile-response"
      this.element.appendChild(input)
    }
    input.value = token || ""
  }

  currentToken() {
    return this.element.querySelector("input[name='cf-turnstile-response']")?.value?.trim()
  }

  formReady() {
    return this.emailValid() && this.consentTarget.checked
  }

  emailValid() {
    return EMAIL_PATTERN.test(this.emailTarget.value.trim())
  }

  showEmailError() {
    if (!this.hasEmailErrorTarget) return

    this.emailErrorTarget.textContent = EMAIL_ERROR_MESSAGE
    this.emailErrorTarget.classList.remove("hidden")
    this.emailTarget.setAttribute("aria-invalid", "true")
    this.emailTarget.setAttribute("aria-describedby", this.emailErrorTarget.id)
  }

  clearEmailError() {
    if (!this.hasEmailErrorTarget) return

    this.emailErrorTarget.textContent = ""
    this.emailErrorTarget.classList.add("hidden")
    this.emailTarget.removeAttribute("aria-invalid")
    this.emailTarget.removeAttribute("aria-describedby")
  }
}
